<template>
  <ul class="tw:m-0 tw:p-0 tw:flex tw:flex-col">
    <li v-for="(item, index) in list" :key="item.name + '-' + index" class="MainMenuLink tw:py-1.5">
      <MainMenuWorkspaceButton v-if="item.name === 'workspace'" @click="$emit('click', item)" />

      <div v-else>
        <MainMenuLinkButton
          :active="getActiveStatus(item)"
          :active-children="hasActiveChildren(item)"
          :is-mobile="true"
          :item="item"
          :show-children="expandedItems.has(item.name)"
          @click="$emit('click', item)"
        >
          <template v-if="getBadge(item)" #badge>
            <EdroneBadge
              v-if="item.name === 'inbox'"
              :label="getBadge(item)"
              :emphasis="getBadge(item) !== '0' ? 'high' : 'low'"
              size="small"
              type="info"
            />
            <EdroneBadge
              v-else-if="item.name === 'segments'"
              :label="getBadge(item)"
              :emphasis="getBadge(item) !== '0' ? 'high' : 'low'"
              size="small"
              type="info"
            />
            <EdroneBadge
              v-else-if="item.name === 'engagements'"
              :label="getBadge(item)"
              :type="getBadge(item)?.startsWith('0') ? 'default' : 'success'"
              size="small"
            />
            <EdroneBadge
              v-else-if="item.name === 'custom-flows'"
              :label="getBadge(item)"
              :type="getBadge(item)?.startsWith('0') ? 'default' : 'success'"
              size="small"
            />
            <EdroneBadge
              v-else
              :icon="
                getBadge(item) === '1' || getBadge(item) === 'ACTIVE'
                  ? 'fa-light fa-check'
                  : getBadge(item) === 'EXPIRED'
                  ? 'fa-light fa-hourglass-end'
                  : 'fa-light fa-minus'
              "
              :type="
                getBadge(item) === '1' || getBadge(item) === 'ACTIVE'
                  ? 'success'
                  : getBadge(item) === 'EXPIRED'
                  ? 'orange'
                  : 'default'
              "
              size="small"
            ></EdroneBadge>
          </template>
        </MainMenuLinkButton>

        <transition
          enter-active-class="tw:transition-all tw:duration-300 tw:ease-in-out"
          enter-from-class="tw:max-h-0"
          enter-to-class="tw:max-h-96"
          leave-active-class="tw:transition-all tw:duration-300 tw:ease-in-out"
          leave-from-class="tw:max-h-96"
          leave-to-class="tw:max-h-0"
        >
          <div v-if="item.children?.length && expandedItems.has(item.name)" class="tw:overflow-hidden">
            <div class="tw:flex tw:relative">
              <div class="tw:w-px tw:mt-2 tw:bg-default-200 tw:ms-4 tw:mr-1.5"></div>
              <div class="tw:flex-1 tw:pt-2">
                <MainMenuMobileLinkList
                  :expanded-items="expandedItems"
                  :list="filteredChildren(item)"
                  :menu-badges="menuBadges"
                  @click="$emit('click', $event)"
                  @toggle="$emit('toggle', $event)"
                />
              </div>
            </div>
          </div>
        </transition>
      </div>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import { useRoute } from "vue-router";
import { MenuItem, isActive, getBadgeText } from "vue-app/components/MainMenu/MainMenu.helpers.ts";
import { DTOMenuIndicatorResponse } from "vue-app/api/dto";
import MainMenuLinkButton from "vue-app/components/MainMenu/MainMenuLinkButton.vue";
import MainMenuWorkspaceButton from "vue-app/components/MainMenu/MainMenuWorkspaceButton.vue";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";

const props = defineProps<{
  list: MenuItem[];
  expandedItems: Set<string>;
  menuBadges?: DTOMenuIndicatorResponse | null;
}>();

defineEmits<{
  click: [item: MenuItem];
  toggle: [itemName: string];
}>();

const route = useRoute();
const isDev = window.EDRONE_CONTEXT.user.isDev;

function getActiveStatus(item: MenuItem) {
  if (!item.path && !item.children?.length) {
    return false;
  }
  return isActive(route.path, item) || (item.children && checkChildren(item.children));
}

function hasActiveChildren(item: MenuItem) {
  return item.children && checkChildren(item.children);
}

function checkChildren(children: MenuItem[]): boolean {
  return children.some((child) => isActive(route.path, child) || (child.children && checkChildren(child.children)));
}

function filteredChildren(item: MenuItem) {
  if (!item.children) return [];
  return item.children.filter((child) => !child.hidden || isDev);
}

function getBadge(item: MenuItem) {
  return getBadgeText(item.name, props.menuBadges);
}
</script>
